import type { Tutor } from '@brighterly/lib-core-types/tutor/interfaces'
import type { TutorKanbanStage } from '@brighterly/lib-core-types/tutor-kanban/enums'

import { useCommonStore } from '@shared/model'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTutorKanbanStore } from '../model/store'

export interface UpdateTutorPayload {
  stage: TutorKanbanStage | null
  kanban_priority: number | null
  stage_deadline: string | null
  agent_id: number | null
  stage_details?: {
    reasons: string[]
    categories: string[]
  }
}

interface UpdateTutorApiOptions {
  tutorId: string | number
  payload: UpdateTutorPayload
  onStart?: () => void
  onSuccess?: (data: Tutor) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useUpdateTutorApi = async (options: UpdateTutorApiOptions): Promise<Tutor | null> => {
  const { $axios } = useNuxtApp()

  const url = useAdminRoutes('admin.tutor-kanban.update', { tutor_id: options.tutorId })

  const tutorKanbanStore = useTutorKanbanStore()
  const commonStore = useCommonStore()
  const toast = useToast()

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.patch(url!, { tutor: options.payload })
    const tutor: Tutor = data.data.tutor

    tutorKanbanStore.setTutor(tutor)
    commonStore.setLoading(false)
    toast.success('Tutor updated successfully')

    options.onSuccess?.(tutor)

    return tutor
  } catch (err: unknown) {
    console.error(err)
    commonStore.setLoading(false)
    toast.error('Failed to update tutor')
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}
